import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { get, post, uri } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { showErrorToast, showWarningToast } from "helpers/toastMessages";
import SummaryCards from "components/SummaryCards";
import CustomPagination from "components/CustomPagination";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formatToPascal } from "helpers/utils/nameUtils";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";

// Custom debounce hook for search input
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const EmployeeTimesheet = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetCount, setTimesheetCount] = useState({
    totalHours: 0,
    totalSubmitted: 0,
    totalApproved: 0,
    totalRejected: 0,
    totalDraft: 0,
  });
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebounce(searchInput, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [projectsMap, setProjectsMap] = useState(new Map());
  const [assignmentsMap, setAssignmentsMap] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectsFetched, setProjectsFetched] = useState(false);
  const [assignmentsFetched, setAssignmentsFetched] = useState(false);
  const [timesheetFiles, setTimesheetFiles] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "submissionDate",
    direction: "DESC",
  });
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const employeeId = secureLocalStorage.getItem("employeeId");

  // Reset page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedWeek, sortConfig]);

  // Fetch project data
  const fetchAllProjects = useCallback(async () => {
    if (projectsFetched) return;
    setError(null);
    try {
      const response = await get(api.GET_ALL_PROJECTS_ID_NAMES);
      const projectsData =
        response?.paginationResponse?.data || response?.data || [];
      console.log("Projects API response:", projectsData);
      if (!Array.isArray(projectsData)) {
        throw new Error("Invalid projects data format");
      }
      const projectMap = new Map(
        projectsData
          .filter((proj) => proj.projectId && proj.projectName)
          .map((proj) => [proj.projectId, formatToPascal(proj.projectName)])
      );
      console.log("Projects Map:", projectMap);
      setProjectsMap(projectMap);
      setProjectsFetched(true);
    } catch (err) {
      console.error("Error fetching projects:", {
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError("Failed to fetch projects. Check console for details.");
      showErrorToast("Failed to fetch projects.");
      setProjectsMap(new Map());
    }
  }, [projectsFetched]);

  // Fetch assignment data
  const fetchAllAssignments = useCallback(async () => {
    if (assignmentsFetched) return;
    setError(null);
    try {
      const response = await get(api.GET_ALL_ASSIGNMENTS_ID_NAME);
      const assignmentsData = response?.data || [];
      console.log("Assignments API response:", assignmentsData);
      if (!Array.isArray(assignmentsData)) {
        throw new Error("Invalid assignments data format");
      }
      const assignmentMap = new Map(
        assignmentsData
          .filter((assign) => assign.assignmentId && assign.assignmentTitle)
          .map((assign) => [
            assign.assignmentId,
            formatToPascal(assign.assignmentTitle),
          ])
      );
      console.log("Assignments Map:", assignmentMap);
      setAssignmentsMap(assignmentMap);
      setAssignmentsFetched(true);
    } catch (err) {
      console.error("Error fetching assignments:", {
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError("Failed to fetch assignments. Check console for details.");
      showErrorToast("Failed to fetch assignments.");
      setAssignmentsMap(new Map());
    }
  }, [assignmentsFetched]);

  // Fetch timesheet counts
  const fetchCount = useCallback(async () => {
    if (!employeeId) {
      console.error("No employeeId found in secureLocalStorage");
      setError("Employee ID is missing. Please log in again.");
      showErrorToast("Employee ID is missing.");
      return;
    }

    setError(null);
    try {
      const endpoint = api.GET_TIMESHEET_COUNT(parseInt(employeeId));
      console.log("Fetching count from:", endpoint);
      const response = await get(endpoint);
      console.log("Timesheet count response:", response);

      const counts = response?.data?.[0]?.employeeCounts || {};
      setTimesheetCount({
        totalHours: Number(counts.totalHours) || 0,
        totalSubmitted: Number(counts.totalSubmitted) || 0,
        totalApproved: Number(counts.totalApproved) || 0,
        totalRejected: Number(counts.totalRejected) || 0,
        totalDraft: Number(counts.totalDraft) || 0,
      });
    } catch (err) {
      const status = err.response?.status;
      let errorMessage = "Failed to fetch timesheet counts.";
      if (status === 404) {
        errorMessage = "Timesheet count endpoint not found.";
      } else if (status === 401) {
        errorMessage = "Unauthorized. Please check your authentication.";
      } else if (status === 500) {
        errorMessage = "Server error. Please try again later.";
      }

      console.error("Error fetching timesheet count:", {
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status,
      });

      setError(`${errorMessage} Check console for details.`);
      showErrorToast(errorMessage);
      setTimesheetCount({
        totalHours: 0,
        totalSubmitted: 0,
        totalApproved: 0,
        totalRejected: 0,
        totalDraft: 0,
      });
    }
  }, [employeeId]);

  // Fetch timesheets
  const fetchTimesheets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let weekStartISO = null;
      let weekEndISO = null;

      if (
        selectedWeek &&
        selectedWeek instanceof Date &&
        !isNaN(selectedWeek)
      ) {
        const weekStart = new Date(selectedWeek);
        weekStart.setHours(0, 0, 0, 0);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        weekStartISO = weekStart.toISOString().split("T")[0];
        weekEndISO = weekEnd.toISOString().split("T")[0];

        console.log("Calculated week range:", weekStartISO, weekEndISO);
      }

      const payload = {
        searchKey: searchQuery.trim() || "",
        page: currentPage - 1,
        pageSize: 10,
        sortBy: sortConfig.sortBy,
        direction: sortConfig.direction,
        timesheetDTO: {
          subscriptionId: 100,
          employeeId: employeeId,
          ...(weekStartISO && weekEndISO
            ? {
                periodStart: weekStartISO,
                periodEnd: weekEndISO,
              }
            : {}),
        },
      };

      console.log("Fetching timesheets with payload:", payload);
      const response = await post(api.GET_ALL_TIMESHEETS_BY_SEARCH, payload);
      console.log("Timesheets API response:", response);

      const empData = response?.data?.employeeTimesheets?.data || [];

      // Fetch file details for timesheets with clientDocuments
      const timesheetDoc = empData
        .filter((item) => item.clientDocuments)
        .map((item) => item.clientDocuments);

      const fileDetails = await Promise.all(
        timesheetDoc.map(async (fileId) => {
          try {
            const fileResponse = await get(
              `${uri}/fileservice/api/files/view/${fileId}`
            );
            return {
              id: fileResponse.id,
              fileName: fileResponse.fileName,
              name: fileResponse.fileName,
              path: fileResponse.path || `/Uploads/${fileResponse.fileName}`,
            };
          } catch (error) {
            console.error(`Error fetching file ${fileId}:`, error);
            return null;
          }
        })
      );
      setTimesheetFiles(fileDetails.filter((file) => file !== null));

      if (!empData.length) {
        console.log(
          "No employee timesheets returned for search query:",
          searchQuery
        );
      }

      const processedEmpData = empData.map((ts) => {
        const projectId =
          ts.projectId && !isNaN(ts.projectId) && ts.projectId > 0
            ? ts.projectId
            : null;
        const projectName =
          projectId && projectsMap.has(projectId)
            ? projectsMap.get(projectId)
            : projectId
            ? formatToPascal(`Unknown Project ${projectId}`)
            : "N/A";

        const assignmentId =
          ts.assignmentId && !isNaN(ts.assignmentId) && ts.assignmentId > 0
            ? ts.assignmentId
            : null;
        const assignmentTitle =
          assignmentId && assignmentsMap.has(assignmentId)
            ? assignmentsMap.get(assignmentId)
            : assignmentId
            ? formatToPascal(`Unknown Assignment ${assignmentId}`)
            : "N/A";

        return {
          ...ts,
          employeeName: formatToPascal(ts.employeeName || "N/A"),
          projectId: projectId || null,
          projectName,
          assignmentId: assignmentId || null,
          assignmentTitle,
        };
      });

      setTimesheets(processedEmpData);
      setTotalPages(response?.data?.employeeTimesheets?.totalPages || 0);

      if (!empData.length) {
        showWarningToast(
          "No timesheets found for the selected filters or search query."
        );
      }
    } catch (error) {
      console.error("Error fetching timesheets:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to fetch timesheets. Check console for details.");
      showErrorToast("Failed to fetch timesheets.");
      setTimesheets([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    searchQuery,
    currentPage,
    sortConfig,
    employeeId,
    projectsMap,
    assignmentsMap,
    selectedWeek,
  ]);

  // Handle file download
  const handleDownloadFile = async (timesheet) => {
    const fileId = timesheet.clientDocuments;
    console.log("Attempting to download file with ID:", fileId);

    if (!fileId) {
      showWarningToast("No file associated with this timesheet.");
      return;
    }

    const file = timesheetFiles.find((f) => f.id == fileId);
    console.log("File Details:", file);
    if (!file) {
      showErrorToast("File details not found.");
      return;
    }

    const fileExtension = file.fileName.split(".").pop().toLowerCase();
    const mimeTypes = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      doc: "application/msword",
    };
    const mimeType = mimeTypes[fileExtension] || "application/octet-stream";

    try {
      const response = await axios.get(
        `${uri}/fileservice/api/files/download/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response?.data], { type: mimeType });
      console.log("Blob Size:", blob.size);

      if (blob.size === 0) {
        showErrorToast("Downloaded file is empty.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", {
        message: err.message,
        stack: err.stack,
        response: err.response,
        status: err.response?.status,
      });
      let errorMessage = "Failed to download file.";
      if (err.response?.status === 404) {
        errorMessage = "File not found on the server.";
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized access. Please check your authentication.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
      showErrorToast(errorMessage);
    }
  };

  // Handle sort
  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      const newConfig =
        prevConfig.sortBy === columnName
          ? {
              sortBy: columnName,
              direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
            }
          : {
              sortBy: columnName,
              direction: "ASC",
            };
      console.log("New sort config:", newConfig);
      setCurrentPage(1); // Reset to page 1
      return newConfig;
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  const columnSortMap = {
    "Timesheet Code": "timesheetCode",
    "Period/Week": "periodStart",
    "Employee Name": "employeeName",
    Project: "projectId",
    Assignment: "assignmentId",
    "Total Hours": "totalHours",
    Status: "status",
    "Submission Date": "submissionDate",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Timesheet Code",
          "Period/Week",
          "Employee Name",
          "Project",
          "Assignment",
          "Total Hours",
          "Status",
          "Submission Date",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  // Initial data fetches
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchAllProjects(), fetchAllAssignments()]);
        await Promise.all([fetchTimesheets(), fetchCount()]);
        setInitialFetchDone(true);
      } catch (err) {
        console.error("Initial fetch failed:", err);
        setError("Failed to load initial data. Please try again.");
        showErrorToast("Failed to load initial data.");
      }
    };
    if (!initialFetchDone) {
      fetchInitialData();
    }
  }, [fetchAllProjects, fetchAllAssignments, fetchTimesheets, fetchCount, initialFetchDone]);

  // Fetch timesheets when filters or sort change
  useEffect(() => {
    if (initialFetchDone) {
      fetchTimesheets();
    }
  }, [fetchTimesheets, initialFetchDone]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const weekNumber = getWeekNumber(start);
      return `Week ${weekNumber} / ${start.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
      })}-${end.toLocaleDateString("en-US", { day: "2-digit" })}`;
    } catch {
      return "Invalid Date";
    }
  };

  // Calculate week number
  const getWeekNumber = (date) => {
    try {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    } catch {
      return 0;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "text-success";
      case "SUBMITTED":
        return "text-warning";
      case "REJECTED":
        return "text-danger";
      case "DRAFT":
        return "text-muted";
      default:
        return "text-muted";
    }
  };

  // Summary card data
  const summaryData = [
    {
      label: "Total Hours",
      value: timesheetCount.totalHours || 0,
      iconClass: "fas fa-clock text-primary",
    },
    {
      label: "Submitted",
      value: timesheetCount.totalSubmitted || 0,
      iconClass: "fas fa-paper-plane text-warning",
    },
    {
      label: "Approved",
      value: timesheetCount.totalApproved || 0,
      iconClass: "fas fa-check-circle text-primary",
    },
    {
      label: "Rejected",
      value: timesheetCount.totalRejected || 0,
      iconClass: "fas fa-times-circle text-danger",
    },
    {
      label: "Draft",
      value: timesheetCount.totalDraft || 0,
      iconClass: "fas fa-save text-primary",
    },
  ];

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading="Timesheets Management"
          routePath="Home"
          pageName="My Timesheets"
        />
        <Row className="px-4">
          {error && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}
          <SummaryCards data={summaryData} />
          <Row className="py-1 px-4">
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2">
                <Col xs={12} sm={4}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search Timesheets"
                      className="elbert-search form-control"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      aria-label="Search timesheets"
                    />
                    <InputGroupText>
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-search" />
                      )}
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={12} sm={4}>
                  <FormGroup>
                    <Label className="fw-bold">Select Week</Label>
                    <DatePicker
                      selected={selectedWeek}
                      onChange={(date) => setSelectedWeek(date)}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      showWeekNumbers
                      placeholderText="Select a week"
                      aria-label="Select week"
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col
                  xs={12}
                  sm={3}
                  className="d-flex align-items-center justify-content-sm-end justify-content-center mt-2 mt-sm-0"
                >
                  <CustomButton
                    text="+ Add New"
                    onClick={() => navigate("/add-timesheet")}
                    aria-label="Add new timesheet"
                  />
                </Col>
              </Row>
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <colgroup>
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "15%" }} />
                      </colgroup>
                      {renderTableHeader()}
                      <tbody>
                        {timesheets.map((timesheet) => (
                          <tr
                            key={
                              timesheet.timesheetId ||
                              timesheet.id ||
                              Math.random()
                            }
                          >
                            <td style={{ width: "12%" }}>
                              {timesheet.timesheetCode || "N/A"}
                            </td>
                            <td style={{ width: "13%" }}>
                              {formatDateRange(
                                timesheet.periodStart,
                                timesheet.periodEnd
                              )}
                            </td>
                            <td style={{ width: "16%" }}>
                              {formatToPascal(timesheet.employeeName) || "N/A"}
                            </td>
                            <td style={{ width: "16%" }}>
                              {formatToPascal(timesheet.projectName) ||
                                (timesheet.projectId
                                  ? `Project ID ${timesheet.projectId}`
                                  : "N/A")}
                            </td>
                            <td style={{ width: "16%" }}>
                              {formatToPascal(timesheet.assignmentTitle) ||
                                (timesheet.assignmentId
                                  ? `Assignment ID ${timesheet.assignmentId}`
                                  : "N/A")}
                            </td>
                            <td style={{ width: "8%" }}>
                              {timesheet.totalHours || 0}
                            </td>
                            <td
                              style={{ width: "10%" }}
                              className={getStatusColor(timesheet.status)}
                            >
                              {timesheet.status
                                ? timesheet.status.charAt(0).toUpperCase() +
                                  timesheet.status.slice(1).toLowerCase()
                                : "N/A"}
                            </td>
                            <td style={{ width: "10%" }}>
                              {timesheet.submissionDate
                                ? new Date(
                                    timesheet.submissionDate
                                  ).toLocaleDateString("en-US")
                                : "-"}
                            </td>
                            <td style={{ width: "15%" }}>
                              <div className="d-flex gap-3 justify-content-center align-items-center">
                                <i
                                  onClick={() =>
                                    navigate(
                                      `/view-timesheet/${
                                        timesheet.timesheetId ||
                                        timesheet.id ||
                                        ""
                                      }`
                                    )
                                  }
                                  className="fa-solid fa-eye action-icon"
                                  title="View"
                                  aria-label={`View timesheet ${
                                    timesheet.timesheetCode || "unknown"
                                  }`}
                                />
                                {["DRAFT", "SUBMITTED", "REJECTED"].includes(
                                  timesheet.status?.toUpperCase() || ""
                                ) && (
                                  <i
                                    onClick={() =>
                                      navigate(
                                        `/add-timesheet/${
                                          timesheet.timesheetId ||
                                          timesheet.id ||
                                          ""
                                        }`
                                      )
                                    }
                                    className="fa-regular fa-pen-to-square action-icon"
                                    title="Edit"
                                    aria-label={`Edit timesheet ${
                                      timesheet.timesheetCode || "unknown"
                                    }`}
                                  />
                                )}
                                {timesheet.clientDocuments && (
                                  <i
                                    onClick={() =>
                                      handleDownloadFile(timesheet)
                                    }
                                    className="fa-solid fa-download action-icon"
                                    title="Download"
                                    aria-label={`Download file for timesheet ${
                                      timesheet.timesheetCode || "unknown"
                                    }`}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {isLoading ? (
                      <div className="text-center py-3">
                        <i className="fas fa-spinner fa-spin" /> Loading
                        timesheets...
                      </div>
                    ) : timesheets.length === 0 ? (
                      <div className="text-center py-3">
                        {searchQuery.trim()
                          ? `No timesheets found for search query "${searchQuery}".`
                          : "No timesheets found for the selected filters."}
                      </div>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            aria-label="Timesheet pagination"
          />
        </Row>
      </Container>
    </div>
  );
};

export default EmployeeTimesheet;